import { decodeToken, isExpired } from 'react-jwt';
import { AESDecryption } from './Aes';

const SESSION_EXPIRED_EVENT = 'mogu:session-expired';

let notified = false;
let expiryTimerId = null;

const AUTH_URL_SKIP_PATTERNS = [
  '/api/auth/login',
  '/api/auth/forgot',
  '/api/auth/reset',
  '/api/auth/changepassword',
  '/api/auth/firsttime',
];

export const getStoredJwt = () => {
  const encrypted = localStorage.getItem('token');
  if (!encrypted) {
    return null;
  }

  try {
    const token = AESDecryption(encrypted);
    return token || null;
  } catch {
    return null;
  }
};

export const isStoredJwtExpired = () => {
  const token = getStoredJwt();
  if (!token) {
    return false;
  }
  return isExpired(token);
};

export const hasStoredSession = () => Boolean(localStorage.getItem('token'));

export const shouldIgnoreUnauthorizedUrl = (url = '') => {
  const requestUrl = String(url).toLowerCase();
  return AUTH_URL_SKIP_PATTERNS.some((pattern) => requestUrl.includes(pattern));
};

export const notifySessionExpired = () => {
  if (notified || !hasStoredSession()) {
    return;
  }
  notified = true;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
};

export const subscribeSessionExpired = (handler) => {
  window.addEventListener(SESSION_EXPIRED_EVENT, handler);
  return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handler);
};

export const resetSessionExpiredNotice = () => {
  notified = false;
};

export const clearSessionExpiryTimer = () => {
  if (expiryTimerId) {
    clearTimeout(expiryTimerId);
    expiryTimerId = null;
  }
};

export const scheduleSessionExpiryWatch = () => {
  clearSessionExpiryTimer();

  const token = getStoredJwt();
  if (!token || isExpired(token)) {
    if (token) {
      notifySessionExpired();
    }
    return;
  }

  const decoded = decodeToken(token);
  const expiresAtMs = decoded?.exp ? decoded.exp * 1000 : null;
  if (!expiresAtMs) {
    return;
  }

  const delayMs = Math.max(expiresAtMs - Date.now(), 0);
  expiryTimerId = setTimeout(() => {
    notifySessionExpired();
  }, delayMs);
};

export const assertAuthorizedFetchResponse = (response) => {
  if (!response) {
    throw new Error('No response from server');
  }

  if (response.status === 401) {
    notifySessionExpired();
    const error = new Error('Session expired');
    error.status = 401;
    throw error;
  }

  if (!response.ok) {
    const error = new Error(`Request failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }
};
